/**
 * Shared Field-Data column-config engine — the ONE place that turns a job's
 * Sample Field Configuration (SamplingFieldConfiguration on its VisualMatrix)
 * into grid columns, so Field Data Entry, Validation AND Tracking all render the
 * SAME captions / visibility / order / width / mandatory markers and the SAME
 * dynamic (config-driven) extra columns (user request 2026-09-02 — the config
 * must affect all three forms, not just Entry).
 *
 * Extracted verbatim from FieldDataEntryPage so there is a single implementation
 * (standing rule 8). Entry keeps editable cells; the read-only Validation /
 * Tracking views pass their assembled columns through `stripEditable`.
 */
import type { ReactNode } from 'react';
import type { AppColumn } from '../../components/AppDataGrid';
import { fmtLocalDate } from '../../utils/datetime';

// ─── Field Config (SamplingFieldConfiguration) ────────────────────────────────
export interface FieldConfigItem {
  fieldId:     string;
  caption:     string;
  width:       number;
  sortOrder:   number;
  freeze:      boolean;
  isMandatory: boolean;
  isReadonly:  boolean;
  fieldClass:  number; // 0=Sampling, 1=Station, 2=Monitoring
}

// ─── Dynamic (config-driven) extra columns bound to real SampleLogIn /
// SampleParameter columns — any configured field beyond the fixed typed set. ──
export interface DynamicColumn {
  fieldId: string;
  caption: string;
  width: number;
  sortOrder: number;
  freeze: boolean;
  isMandatory: boolean;
  isReadonly: boolean;
  options?: string[] | null; // lookup (FK) fields → combo options
  editorType?: string;       // 'combo' | 'date' | 'checkbox' | 'number' | 'text' (from SQL type / FK)
}
export interface DynamicFieldSet {
  columns: DynamicColumn[];
  values: Record<string, Record<string, string | null>>; // values[rowOid][fieldId]
}
export const EMPTY_DYN: DynamicFieldSet = { columns: [], values: {} };

/** Build the dynamic (config-driven) columns bound to real SampleLogIn /
 *  SampleParameter columns. */
export function buildDynamicColumns<T extends Record<string, unknown>>(dyn: DynamicFieldSet): AppColumn<T>[] {
  return dyn.columns.map(c => {
    // Editor from the column's SQL type / FK (XAF-parity): combo (FK) · date ·
    // checkbox (bit) · number · text.  Falls back to combo-if-options / text.
    const editor = c.editorType ?? ((c.options && c.options.length) ? 'combo' : 'text');
    const render = (r: T): ReactNode => {
      const v = r[c.fieldId];
      if (v == null || v === '') return <span></span>;
      if (editor === 'date')     return <span>{fmtLocalDate(String(v))}</span>;
      if (editor === 'checkbox') return <span>{String(v) === 'true' ? '☑' : '☐'}</span>;
      return <span>{String(v)}</span>;
    };
    let edit: AppColumn<T>['edit'] | undefined;
    if (!c.isReadonly) {
      if (editor === 'combo')         edit = { type: 'combo', options: c.options ?? [], trigger: 'click' };
      else if (editor === 'date')     edit = { type: 'date', trigger: 'click' };
      else if (editor === 'checkbox') edit = { type: 'combo', options: ['true', 'false'], trigger: 'click' };
      else                            edit = { type: 'text', trigger: 'click' }; // text + number
    }
    return {
      field: c.fieldId,
      header: c.isMandatory ? (<>{c.caption} <span className="req-star">*</span></>) : c.caption,
      headerText: c.isMandatory ? `${c.caption} *` : c.caption,
      width: c.width > 0 ? c.width : 120,
      render,
      ...(edit ? { edit } : {}),
    } as AppColumn<T>;
  });
}

/** The SortOrder each AI column key carries in config — fixed (mapped) + dynamic.
 *  Columns not in config (AI-only entry cells) fall back to 9999 → end. */
function sortOrderMap(
  config: FieldConfigItem[], fieldClass: number,
  fieldMap: Record<string, string>, dyn: DynamicFieldSet,
): Map<string, number> {
  const m = new Map<string, number>();
  config.filter(c => c.fieldClass === fieldClass).forEach(c => {
    const k = fieldMap[c.fieldId];
    if (k) m.set(k, c.sortOrder);
  });
  dyn.columns.forEach(c => m.set(c.fieldId, c.sortOrder));
  return m;
}

/** Assemble one tab's columns: merge the fixed + dynamic columns, order them ALL
 *  by config SortOrder (ALPACA parity — dynamic fields interleave, not appended),
 *  then pull the frozen ones to the front (sticky). */
export function assembleTab<T extends Record<string, unknown>>(
  fixed: AppColumn<T>[], dyn: DynamicFieldSet,
  config: FieldConfigItem[], fieldClass: number, fieldMap: Record<string, string>,
): { cols: AppColumn<T>[]; sticky: number } {
  const merged = [...fixed, ...buildDynamicColumns<T>(dyn)];
  const order = sortOrderMap(config, fieldClass, fieldMap, dyn);
  merged.sort((a, b) => (order.get(a.field) ?? 9999) - (order.get(b.field) ?? 9999));
  return applyFreeze(merged, frozenFields(config, fieldClass, fieldMap, dyn));
}

/** Merge a dynamic value set onto rows keyed by `rowKey` (so the values live on
 *  the row object and become editable grid cells). */
export function mergeDynamic<T extends Record<string, unknown>>(
  rows: T[], dyn: DynamicFieldSet, rowKey: (r: T) => string,
): T[] {
  if (dyn.columns.length === 0) return rows;
  return rows.map(r => {
    const v = dyn.values[rowKey(r)];
    return v ? { ...r, ...v } : r;
  });
}

/** The AI column keys the matrix marked Freeze — fixed (mapped from a legacy
 *  FieldID) + dynamic. */
function frozenFields(
  config: FieldConfigItem[], fieldClass: number,
  fieldMap: Record<string, string>, dyn: DynamicFieldSet,
): Set<string> {
  const s = new Set<string>();
  config.filter(c => c.fieldClass === fieldClass && c.freeze).forEach(c => {
    const k = fieldMap[c.fieldId];
    if (k) s.add(k);
  });
  dyn.columns.filter(c => c.freeze).forEach(c => s.add(c.fieldId));
  return s;
}

/** Move the frozen columns to the front (ALPACA `FixedStyle.Left` parity) and
 *  return the count to feed the grid's `stickyColumns` so they pin while the
 *  rest scroll horizontally. */
function applyFreeze<T>(
  cols: AppColumn<T>[], frozen: Set<string>,
): { cols: AppColumn<T>[]; sticky: number } {
  if (frozen.size === 0) return { cols, sticky: 0 };
  const fz = cols.filter(c => frozen.has(c.field));
  const rest = cols.filter(c => !frozen.has(c.field));
  // Frozen columns need a numeric width for the sticky offset maths.
  const widen = (c: AppColumn<T>): AppColumn<T> =>
    typeof c.width === 'number' ? c : { ...c, width: 120 };
  return { cols: [...fz.map(widen), ...rest], sticky: fz.length };
}

// Legacy FieldID → AI column key, per tab (FieldClass)
export const SAMPLING_FIELD_MAP: Record<string, string> = {
  'StationLocation':  'stationName',
  'SampleID':         'sampleId',
  'Collector':        'collectorName',
  'CollectDate':      'collectedDate',
  'FieldTestSummary': 'testSummary',
  'samplecomment':    'sampleComment',
  'EnteredBy':        'enteredBy',
  'EnteredDate':      'enteredDate',
};
export const STATION_FIELD_MAP: Record<string, string> = {
  'JobID':           'stationId',
  'StationLocation': 'stationName',
  'Blended':         'blended',
  'Latitude':        'latitude',
  'Longitude':       'longitude',
  'Temp':            'temperature',
  'Humidity':        'humidity',
  'stationcommment': 'stationComment',
};
export const MONITORING_FIELD_MAP: Record<string, string> = {
  'Samplelogin':         'sampleId',
  'Test':                'test',
  'MethodName':          'methodName',
  'Parameter':           'parameter',
  'Result':              'result',
  'SampleparameterUnit': 'unit',
};

// Auto-stamped columns — never manually editable (the app fills EnteredBy /
// EnteredDate / MonitoredBy / MonitoredDate on edit, ALPACA parity).
export const AUTO_MANAGED_COLS = new Set<string>(['enteredBy', 'enteredDate', 'monitoredBy', 'monitoredDate']);

/** Build Map<aiColumnKey, FieldConfigItem> for a given tab class. */
function buildConfigMap(
  config: FieldConfigItem[],
  fieldClass: number,
  fieldMap: Record<string, string>,
): Map<string, FieldConfigItem> {
  const map = new Map<string, FieldConfigItem>();
  config.filter(c => c.fieldClass === fieldClass).forEach(c => {
    const key = fieldMap[c.fieldId];
    if (key) map.set(key, c);
  });
  return map;
}

/**
 * Apply the Sample Field Configuration to a tab's columns — ALPACA
 * `FieldDataEntryViewController.customisefields` parity.
 *
 * When the job's matrix has field config for this tab, show ONLY the SELECTED
 * columns (config-driven visibility — every column not in config is hidden),
 * ordered by SortOrder, with the configured Caption / Width / read-only, and a
 * red `*` for mandatory fields. When there is no config (empty), the tab keeps
 * its default column set unchanged (matches the SP's "no matrix → defaults").
 */
export function applyFieldConfig<T>(
  base: AppColumn<T>[],
  config: FieldConfigItem[],
  fieldClass: number,
  fieldMap: Record<string, string>,
): AppColumn<T>[] {
  const cfgMap = buildConfigMap(config, fieldClass, fieldMap);
  if (cfgMap.size === 0) return base; // no config → default columns

  // AI columns that ARE part of the config vocabulary (mapped from a legacy
  // FieldID). A configurable column shows ONLY when its matrix selected it;
  // AI-only columns not in the vocabulary (e.g. the Monitoring MonitoredBy /
  // MonitoredDate entry cells) are kept always so data entry never breaks.
  const configurable = new Set<string>(Object.values(fieldMap));

  const applied = base
    .filter(col => !configurable.has(col.field as string) || cfgMap.has(col.field as string))
    .map(col => {
      const cfg = cfgMap.get(col.field as string);
      if (!cfg) return col; // AI-only column — leave untouched
      const label = cfg.caption;
      let col2: AppColumn<T> = {
        ...col,
        width: cfg.width > 0 ? cfg.width : col.width,
        header: cfg.isMandatory
          ? (<>{label} <span className="req-star">*</span></>)
          : label,
        headerText: cfg.isMandatory ? `${label} *` : label,
      };
      if (cfg.isReadonly) {
        const { edit: _e, ...rest } = col2 as typeof col2 & { edit?: unknown };
        col2 = rest as AppColumn<T>;
      } else if (!(col2 as { edit?: unknown }).edit && !AUTO_MANAGED_COLS.has(col.field as string)) {
        // Config says editable (IsReadonly unchecked) but the base column has no
        // editor → give it the default click-to-edit text cell (e.g. SampleComment).
        col2 = { ...col2, edit: { type: 'text', trigger: 'click' } } as AppColumn<T>;
      }
      return col2;
    });

  return applied.sort((a, b) =>
    (cfgMap.get(a.field as string)?.sortOrder ?? 9999) -
    (cfgMap.get(b.field as string)?.sortOrder ?? 9999));
}

/** Strip inline editing from an assembled column set — the read-only Validation
 *  and Tracking views reuse the SAME config engine as Entry but never edit. */
export function stripEditable<T>(cols: AppColumn<T>[]): AppColumn<T>[] {
  return cols.map(c => {
    if (!('edit' in c) || !c.edit) return c;
    const { edit: _e, ...rest } = c as AppColumn<T> & { edit?: unknown };
    return rest as AppColumn<T>;
  });
}
